// src/components/Dashboard.js
import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase/firebase-config";
import { collection, getDocs } from "firebase/firestore";
import { signOut } from "firebase/auth";
import {
  FaCheckCircle,
  FaChartBar,
  FaCode,
  FaGlobe,
  FaHistory,
  FaMapMarkerAlt,
  FaLandmark,
  FaListAlt,
  FaMobileAlt,
  FaPlusCircle,
  FaTachometerAlt,
  FaUniversity,
  FaUsers,
  FaWallet,
  FaWifi,
} from "react-icons/fa";
import {
  clearClientSession,
  markSessionActivity,
  SESSION_IDLE_TIMEOUT_MS,
} from "../utils/sessionSecurity";

const dashboardModules = [
  {
    title: "Overview",
    path: "/dashboard",
    icon: FaTachometerAlt,
    color: "#0f766e",
    background: "linear-gradient(135deg, rgba(15,118,110,0.14), rgba(14,165,233,0.10))",
  },
  {
    title: "All Details",
    path: "/dashboard/all-details",
    icon: FaListAlt,
    color: "#1d4ed8",
    background: "linear-gradient(135deg, rgba(29,78,216,0.14), rgba(59,130,246,0.10))",
  },
  {
    title: "Create Details",
    path: "/dashboard/create-details",
    icon: FaPlusCircle,
    color: "#7c3aed",
    background: "linear-gradient(135deg, rgba(124,58,237,0.14), rgba(168,85,247,0.10))",
  },
  {
    title: "All Sites",
    path: "/dashboard/all-sites",
    icon: FaGlobe,
    color: "#ea580c",
    background: "linear-gradient(135deg, rgba(234,88,12,0.14), rgba(251,146,60,0.10))",
  },
  {
    title: "Format",
    path: "/dashboard/format",
    icon: FaCode,
    color: "#0891b2",
    background: "linear-gradient(135deg, rgba(8,145,178,0.14), rgba(6,182,212,0.10))",
  },
  {
    title: "All Customers",
    path: "/dashboard/All_Customers",
    icon: FaUsers,
    color: "#c2410c",
    background: "linear-gradient(135deg, rgba(194,65,12,0.14), rgba(251,146,60,0.10))",
    targetOnly: true,
  },
  {
    title: "UPI Verify",
    path: "/dashboard/UpiVerify",
    icon: FaCheckCircle,
    color: "#059669",
    background: "linear-gradient(135deg, rgba(5,150,105,0.14), rgba(16,185,129,0.10))",
  },
  {
    title: "Mobile Submitted",
    path: "/dashboard/mobile-bookings-submitted",
    icon: FaMobileAlt,
    color: "#db2777",
    background: "linear-gradient(135deg, rgba(219,39,119,0.14), rgba(244,114,182,0.10))",
  },
  {
    title: "Upgrade Line Form",
    path: "/upgrade-line",
    icon: FaMobileAlt,
    color: "#7c3aed",
    background: "linear-gradient(135deg, rgba(124,58,237,0.14), rgba(168,85,247,0.10))",
  },
  {
    title: "Upgrade Line Submissions",
    path: "/dashboard/upgrade-line",
    icon: FaListAlt,
    color: "#7c3aed",
    background: "linear-gradient(135deg, rgba(124,58,237,0.14), rgba(168,85,247,0.10))",
    targetOnly: true,
  },
  {
    title: "Metro",
    path: "/dashboard/Metro",
    icon: FaWifi,
    color: "#6e2382",
    background: "linear-gradient(135deg, rgba(110,35,130,0.14), rgba(226,0,116,0.10))",
  },
  {
    title: "Get Address",
    path: "/get-address",
    icon: FaMapMarkerAlt,
    color: "#6e2382",
    background: "linear-gradient(135deg, rgba(110,35,130,0.14), rgba(226,0,116,0.10))",
  },
  {
    title: "Used Addresses",
    path: "/used-addresses",
    icon: FaHistory,
    color: "#6e2382",
    background: "linear-gradient(135deg, rgba(110,35,130,0.14), rgba(226,0,116,0.10))",
  },
  {
    title: "Amount",
    path: "/dashboard/MyAmount",
    icon: FaWallet,
    color: "#059669",
    background: "linear-gradient(135deg, rgba(5,150,105,0.14), rgba(16,185,129,0.10))",
    targetOnly: true,
  },
  {
    title: "DailyWiseReport",
    path: "/dashboard/DailyWiseReport",
    icon: FaChartBar,
    color: "#0f766e",
    background: "linear-gradient(135deg, rgba(15,118,110,0.14), rgba(20,184,166,0.10))",
    targetOnly: true,
  },
  {
    title: "BankAccount Details",
    path: "/dashboard/BankAccountDetails",
    icon: FaUniversity,
    color: "#7c2d12",
    background: "linear-gradient(135deg, rgba(124,45,18,0.14), rgba(217,119,6,0.10))",
    targetOnly: true,
  },
  {
    title: "BankAccount Submitted",
    path: "/dashboard/BankAccountSubmitted",
    icon: FaLandmark,
    color: "#0f172a",
    background: "linear-gradient(135deg, rgba(15,23,42,0.12), rgba(100,116,139,0.12))",
    targetOnly: true,
  },
];

const Dashboard = () => {
  const [cardCounts, setCardCounts] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const targetUserId = "o1TwhgGqQ7PjAr6EITaJtDBfBbp1";
  const visibleModules = dashboardModules.filter(
    (module) => !module.targetOnly || userId === targetUserId
  );

  // Fetch card data
  useEffect(() => {
    if (userId) {
      fetchCardCounts(userId);
    }
  }, [userId]);

  const fetchCardCounts = async (userId) => {
    try {
      const querySnapshot = await getDocs(
        collection(db, "credit-card-details")
      );
      const data = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
      const filteredData = data.filter((card) => card.userId === userId);
      const counts = filteredData.reduce((acc, card) => {
        const { cardType, providerBank } = card;
        if (providerBank && cardType) {
          acc[providerBank] = acc[providerBank] || { total: 0, types: {} };
          acc[providerBank].total += 1;
          acc[providerBank].types[cardType] =
            (acc[providerBank].types[cardType] || 0) + 1;
        }
        return acc;
      }, {});

      setCardCounts(counts);
    } catch (error) {
      console.error("Error fetching card data:", error);
    }
  };

  // ⏱ Auto-logout after 24 hr of inactivity
  useEffect(() => {
    let timer;

    const logoutForInactivity = async () => {
      try {
        await signOut(auth);
      } catch (error) {
        console.error("Unable to sign out after inactivity:", error);
      } finally {
        clearClientSession();
        navigate("/login", { replace: true });
      }
    };

    const resetTimer = () => {
      clearTimeout(timer);
      markSessionActivity();
      timer = setTimeout(logoutForInactivity, SESSION_IDLE_TIMEOUT_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resetTimer();
      }
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("click", resetTimer);
    window.addEventListener("touchstart", resetTimer);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    resetTimer();

    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("click", resetTimer);
      window.removeEventListener("touchstart", resetTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [navigate]);


  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Unable to log out:", error);
    } finally {
      clearClientSession();
      navigate("/login", { replace: true });
    }
  };

  const totalCounts = Object.values(cardCounts).reduce(
    (acc, { total, types }) => {
      acc.total += total;
      acc.Visa += types.Visa || 0;
      acc.MasterCard += types.MasterCard || 0;
      acc.Rupay += types.Rupay || 0;
      return acc;
    },
    { total: 0, Visa: 0, MasterCard: 0, Rupay: 0 }
  );

  return (
    <div className="container-fluid px-3 py-4">
      <div className="row">
        {/* Sidebar */}
        <div className="col-md-2 p-0 mb-3 mb-md-0">
          <div className="list-group sticky-top">
            <Link
              to="/dashboard"
              className="list-group-item list-group-item-action"
            >
              Dashboard
            </Link>
            <Link
              to="/dashboard/all-details"
              className="list-group-item list-group-item-action"
            >
              All Details
            </Link>
            <Link
              to="/dashboard/create-details"
              className="list-group-item list-group-item-action"
            >
              Create Details
            </Link>
            <Link
              to="/dashboard/all-sites"
              className="list-group-item list-group-item-action"
            >
              All Sites
            </Link>
            <Link
              to="/dashboard/format"
              className="list-group-item list-group-item-action"
            >
              Format
            </Link>
            <Link
              to="/dashboard/UpiVerify"
              className="list-group-item list-group-item-action"
            >
              UPI Verify
            </Link>
            <Link
              to="/dashboard/mobile-bookings-submitted"
              className="list-group-item list-group-item-action"
            >
              Mobile Submitted
            </Link>
            <Link
              to="/upgrade-line"
              className="list-group-item list-group-item-action"
            >
              Upgrade Line Form
            </Link>
            {userId === targetUserId && (
              <Link to="/dashboard/upgrade-line" className="list-group-item list-group-item-action">
                Upgrade Line Submissions
              </Link>
            )}
            <Link
              to="/dashboard/Metro"
              className="list-group-item list-group-item-action"
            >
              Metro
            </Link>
            <Link
              to="/get-address"
              className="list-group-item list-group-item-action"
            >
              Get Address
            </Link>
            <Link to="/used-addresses" className="list-group-item list-group-item-action">
              Used Addresses
            </Link>
            {userId === targetUserId && (
              <>
                <Link
                  to="/dashboard/MyAmount"
                  className="list-group-item list-group-item-action"
                >
                  Amount
                </Link>
                <Link
                  to="/dashboard/All_Customers"
                  className="list-group-item list-group-item-action"
                >
                  All Customers
                </Link>
                <Link
                  to="/dashboard/DailyWiseReport"
                  className="list-group-item list-group-item-action"
                >
                  DailyWiseReport
                </Link>
                <Link
                  to="/dashboard/BankAccountDetails"
                  className="list-group-item list-group-item-action"
                >
                  BankAccount Details
                </Link>
                <Link
                  to="/dashboard/BankAccountSubmitted"
                  className="list-group-item list-group-item-action"
                >
                  BankAccount Submitted
                </Link>
              </>
            )}

            <button
              className="btn btn-danger mt-3 w-100"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="col-md-10">
          {location.pathname === "/dashboard" && (
            <div className="dashboard-home-stack mt-3">
              <section className="dashboard-module-shell">
                <div className="dashboard-module-header">
                  <div>
                    <h2 className="dashboard-module-title">Modules</h2>
                  </div>
                </div>

                <div className="dashboard-module-grid">
                  {visibleModules.map((module) => {
                    const Icon = module.icon;

                    return (
                      <Link
                        key={module.path}
                        to={module.path}
                        className="dashboard-module-card"
                      >
                        <div
                          className="dashboard-module-icon"
                          style={{
                            color: module.color,
                            background: module.background,
                          }}
                        >
                          <Icon />
                        </div>
                        <div className="dashboard-module-body">
                          <h3>{module.title}</h3>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>

              <div className="table-responsive">
                <table className="table table-bordered table-hover">
                  <thead>
                    <tr>
                      <th>Provider Bank</th>
                      <th>Total Cards</th>
                      <th>Visa</th>
                      <th>MasterCard</th>
                      <th>Rupay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(cardCounts).map(
                      ([bank, details], index) => (
                        <tr
                          key={bank}
                          style={
                            index % 2 === 0
                              ? { backgroundColor: "#f8f9fa" }
                              : { backgroundColor: "#e9ecef" }
                          }
                          onMouseOver={(e) =>
                            (e.target.style.backgroundColor = "#d4edda")
                          }
                          onMouseOut={(e) =>
                            (e.target.style.backgroundColor =
                              index % 2 === 0 ? "#f8f9fa" : "#e9ecef")
                          }
                        >
                          <td>{bank}</td>
                          <td>{details.total}</td>
                          <td>{details.types.Visa || 0}</td>
                          <td>{details.types.MasterCard || 0}</td>
                          <td>{details.types.Rupay || 0}</td>
                        </tr>
                      )
                    )}
                    <tr className="total-row">
                      <td>Total</td>
                      <td>{totalCounts.total}</td>
                      <td>{totalCounts.Visa}</td>
                      <td>{totalCounts.MasterCard}</td>
                      <td>{totalCounts.Rupay}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Nested child route content */}
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
